/**
 * Thread view drawer component for displaying message threads (Slack-style).
 */

import React, { useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/joy';
import { X as CloseIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useMessage,
  useThreadReplies,
  useSendMessage,
  chatKeys,
} from '../../hooks/useChat';
import type { Message, MessageCreatePayload } from '../../types/chat';
import MessageItem from './MessageItem';
import MessageComposer from './MessageComposer';

interface ThreadViewProps {
  open: boolean;
  onClose: () => void;
  channelId: string | null;
  parentMessageId: string | null;
  currentUserId?: string | number;
  onReaction?: (messageId: string, emoji: string) => void;
  onRemoveReaction?: (messageId: string, emoji: string) => void;
}

const ThreadView: React.FC<ThreadViewProps> = ({
  open,
  onClose,
  channelId,
  parentMessageId,
  currentUserId,
  onReaction,
  onRemoveReaction,
}) => {
  const queryClient = useQueryClient();

  // Fetch the parent message
  const { data: parentMessage, isLoading: parentLoading } = useMessage(
    channelId,
    parentMessageId
  );

  // Fetch thread replies
  const { data: replies, isLoading: repliesLoading } = useThreadReplies(
    channelId,
    parentMessageId
  );

  // Send reply mutation
  const sendMessage = useSendMessage();

  // Handle sending a reply in the thread
  const handleSendReply = useCallback(
    async (data: MessageCreatePayload) => {
      if (!channelId || !parentMessageId) return;

      try {
        await sendMessage.mutateAsync({
          channelId,
          data: {
            ...data,
            parent_message_id: parentMessageId,
          },
        });

        // Invalidate thread replies to refresh the list
        queryClient.invalidateQueries({
          queryKey: chatKeys.thread(parentMessageId),
        });

        // Invalidate channel messages to update reply_count on parent
        queryClient.invalidateQueries({
          queryKey: chatKeys.channelMessages(channelId),
        });
      } catch (error) {
        console.error('Failed to send thread reply:', error);
      }
    },
    [channelId, parentMessageId, sendMessage, queryClient]
  );

  const isLoading = parentLoading || repliesLoading;
  const replyCount = replies?.length || 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        content: {
          sx: {
            width: { xs: '100%', sm: 400, md: 450 },
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography level="title-lg">Thread</Typography>
        <IconButton variant="plain" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !parentMessage ? (
          <Typography color="danger" sx={{ textAlign: 'center', py: 4 }}>
            Failed to load thread
          </Typography>
        ) : (
          <>
            {/* Parent Message */}
            <Box sx={{ py: 1 }}>
              <MessageItem
                message={parentMessage}
                isOwnMessage={
                  currentUserId !== undefined &&
                  String(parentMessage.sender?.id) === String(currentUserId)
                }
                hideThreadIndicator
                onReaction={onReaction}
                onRemoveReaction={onRemoveReaction}
              />
            </Box>

            {/* Replies Divider */}
            <Box sx={{ px: 2, py: 1 }}>
              <Divider>
                <Typography level="body-sm" color="neutral">
                  {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                </Typography>
              </Divider>
            </Box>

            {/* Replies List */}
            <Box sx={{ flex: 1 }}>
              {replies && replies.length > 0 ? (
                replies.map((reply: Message) => (
                  <MessageItem
                    key={reply.id}
                    message={reply}
                    isOwnMessage={
                      currentUserId !== undefined &&
                      String(reply.sender?.id) === String(currentUserId)
                    }
                    onReaction={onReaction}
                    onRemoveReaction={onRemoveReaction}
                  />
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
                  <Typography level="body-sm" color="neutral">
                    No replies yet. Be the first to reply!
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Fixed Footer - Message Composer */}
      {channelId && parentMessageId && (
        <Box sx={{ flexShrink: 0 }}>
          <MessageComposer
            channelId={channelId}
            placeholder="Reply in thread..."
            currentUserId={currentUserId}
            onSend={handleSendReply}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default ThreadView;
